import { Alert, Form, Input, Modal, Select, Space, Switch, notification } from 'antd';
import CustomButton from 'components/custom-button';
import { REQUIRED } from 'constants/rule-form';
import { useEffect, useMemo, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from '../styles.module.scss';
import { UPLOAD_TYPE } from 'constants/common';
import CustomizedUpload from 'components/customized-upload';
import { CHECK_FILE } from 'utils/rule-form';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import {
  LINK_PROTECTION,
  LINK_PROTECTION_OPTIONS,
  MENU_TYPE_OPTIONS,
  MenuGroup,
  getErrorMessage,
  isDirectFileLink,
  isInternalMenuLink,
  linkProtectionFields,
  linkProtectionOf,
} from '../constants';
const { Option } = Select;

// Giá trị của lựa chọn "Không nhóm" trong ô chọn; khi gửi lên server đổi thành null.
const NO_GROUP = 'none';

interface DataPost {
  name: string;
  link: string;
  icon: any;
  type: string;
  position: number;
  groupId?: number | typeof NO_GROUP | null;
  isPinned?: boolean;
  /** Chỉ có trong form; gửi lên server thành viewOnly/allowCopyLink/allowOpenBrowser. */
  linkProtection?: LINK_PROTECTION;
}

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

const ModalCreateEdit = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<MenuGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const onFinish = async (values: DataPost) => {
    values.icon = values?.icon?.fileKey;
    values.position = Number(values.position);
    // "Không nhóm" và nút xóa lựa chọn (undefined) đều là null = mục "Khác".
    values.groupId =
      values.groupId === NO_GROUP || values.groupId === undefined || values.groupId === null
        ? null
        : Number(values.groupId);
    values.isPinned = !!values.isPinned;
    const { linkProtection, ...rest } = values;
    const payload = {
      ...rest,
      // Mã màn trong app (Chấm công...) không có trang web để khoá.
      ...linkProtectionFields(
        isInternalMenuLink(rest.link)
          ? LINK_PROTECTION.HIDE_LINK
          : linkProtection ?? LINK_PROTECTION.HIDE_LINK
      ),
    };

    setLoading(true);
    if (valueUserSelected?.id) {
      try {
        const res: any = await AxiosUtils.patch(
          `${API.updateMenu}/${valueUserSelected?.id}`,
          payload
        );
        if (res?.status === 200) {
          handleCancelCreateEditModal();
          onChangeStatusUpdateData();
          notification.success({
            message: 'Thành công',
            description: 'Sửa thành công!',
            duration: 4,
          });
        }
      } catch (error) {
        notification.error({
          message: 'Lỗi',
          description: getErrorMessage(error, 'Không lưu được ứng dụng.'),
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res: any = await AxiosUtils.post(API.updateMenu, payload);
        if (res?.status === 201) {
          handleCancelCreateEditModal();
          onChangeStatusUpdateData();
          notification.success({
            message: 'Thành công',
            description: 'Tạo thành công!',
            duration: 4,
          });
        }
      } catch (error) {
        notification.error({
          message: 'Lỗi',
          description: getErrorMessage(error, 'Không tạo được ứng dụng.'),
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    form.setFieldsValue({
      name: valueUserSelected?.name || '',
      link: valueUserSelected?.link || '',
      position: valueUserSelected?.position || 0,
      icon: valueUserSelected?.icon
        ? {
            fileKey: valueUserSelected?.icon,
            fileUrl: `${SERVER_FILE_ENDPOINT}/${valueUserSelected.icon}`,
          }
        : null,
      type: valueUserSelected?.type || undefined,
      groupId: valueUserSelected?.groupId ?? valueUserSelected?.group?.id ?? NO_GROUP,
      isPinned: !!valueUserSelected?.isPinned,
      // Ứng dụng mới: mặc định "Ẩn link" (vẫn điền được Google Form).
      linkProtection: linkProtectionOf(valueUserSelected),
    });
  }, [form, valueUserSelected]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchGroups = async () => {
      setLoadingGroups(true);
      try {
        const res: any = await AxiosUtils.get(API.menuGroups);
        setGroups(Array.isArray(res?.data) ? res.data : []);
      } catch (error) {
        notification.error({
          message: 'Lỗi',
          description: getErrorMessage(error, 'Không tải được danh sách nhóm.'),
          duration: 4,
        });
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [isOpen]);

  const watchedLink: string | undefined = Form.useWatch('link', form);
  const watchedProtection: LINK_PROTECTION | undefined = Form.useWatch('linkProtection', form);
  const isInternalLink = isInternalMenuLink(watchedLink);
  const selectedProtection = LINK_PROTECTION_OPTIONS.find(
    (option) => option.value === watchedProtection
  );

  const groupOptions = useMemo(() => {
    const list: Array<Pick<MenuGroup, 'id' | 'name'>> = [...groups];
    // Nhóm hiện tại của ứng dụng đang sửa: hiện đúng tên cả khi danh sách nhóm chưa tải xong.
    const currentGroup = valueUserSelected?.group;
    if (currentGroup?.id && !list.some((group) => group.id === currentGroup.id)) {
      list.push(currentGroup);
    }
    return [
      { value: NO_GROUP, label: 'Không nhóm (mục Khác)' },
      ...list.map((group) => ({ value: group.id, label: group.name })),
    ];
  }, [groups, valueUserSelected]);

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        title={valueUserSelected?.id ? 'Sửa ứng dụng' : 'Thêm ứng dụng'}
        onCancel={handleCancelCreateEditModal}
        width={800}
        getContainer={false}
        footer={false}
      >
        <Form
          name="form"
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <label className={styles.labelInput} htmlFor="name">
            Tên
          </label>
          <Form.Item
            name="name"
            rules={[
              REQUIRED,
              {
                max: 100,
                message: 'Không được quá 100 kí tự',
              },
            ]}
            wrapperCol={{ span: 24 }}
          >
            <Input placeholder="Nhập tên" size="large" />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="link">
            Liên kết
          </label>
          <Form.Item name="link" rules={[REQUIRED]} wrapperCol={{ span: 24 }}>
            <Input placeholder="Nhập liên kết" size="large" />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="type">
            Ai thấy
          </label>
          <Form.Item rules={[REQUIRED]} name="type" wrapperCol={{ span: 24 }}>
            <Select placeholder="Chọn ai được thấy ứng dụng" size="large">
              {MENU_TYPE_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <label className={styles.labelInput} htmlFor="groupId">
            Nhóm
          </label>
          <Form.Item name="groupId" wrapperCol={{ span: 24 }}>
            <Select
              placeholder="Chọn nhóm"
              size="large"
              allowClear
              loading={loadingGroups}
              options={groupOptions}
            />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="position">
            Số thứ tự
          </label>
          <Form.Item name="position" rules={[REQUIRED]} wrapperCol={{ span: 24 }}>
            <Input placeholder="Nhập số thứ tự" size="large" />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="isPinned">
            Ghim lên Trang chủ
          </label>
          <Form.Item
            name="isPinned"
            valuePropName="checked"
            wrapperCol={{ span: 24 }}
            extra="Trang chủ app mới hiện 7 ứng dụng được ghim đầu tiên (theo số thứ tự) và ô “Tất cả”."
          >
            <Switch />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="linkProtection">
            Bảo vệ link trên điện thoại
          </label>
          <Form.Item
            name="linkProtection"
            wrapperCol={{ span: 24 }}
            extra={
              isInternalLink
                ? 'Link này mở màn có sẵn trong app (không phải trang web) nên mục này không có tác dụng.'
                : `${selectedProtection?.hint ?? ''} ${
                    watchedProtection === LINK_PROTECTION.VIEW_ONLY
                      ? 'App cũ chưa cập nhật sẽ thấy trang “Cần cập nhật ứng dụng” thay cho nội dung.'
                      : 'App cũ chưa cập nhật vẫn hiện nút ⓘ như trước.'
                  }`
            }
          >
            <Select
              size="large"
              disabled={isInternalLink}
              options={LINK_PROTECTION_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
            />
          </Form.Item>
          {!isInternalLink &&
            watchedProtection === LINK_PROTECTION.VIEW_ONLY &&
            isDirectFileLink(watchedLink) && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Link này là file tải về (PDF, Word, Excel...). App sẽ không mở được ở mức Chỉ xem vì không khoá được file. Hãy dùng link xem trực tuyến, ví dụ Google Drive dạng …/view hoặc Google Docs/Sheets dạng …/edit, …/preview."
              />
            )}

          <label className={styles.labelInput} htmlFor="icon">
            Biểu tượng
          </label>
          <Form.Item
            name="icon"
            wrapperCol={{ span: 24 }}
            rules={[CHECK_FILE]}
            className={styles.iconField}
            extra="Nên dùng ảnh PNG vuông, nét trắng, nền trong suốt — app đặt biểu tượng lên ô hồng như ô xem trước."
          >
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>

          <div className="d-flex justify-center">
            <Space size="large">
              <CustomButton
                bgColor="secondary-green"
                text="Lưu"
                htmlType="submit"
                loading={loading}
              />
              <CustomButton
                text="Hủy"
                bgColor="primary-grey"
                htmlType="button"
                onClick={handleCancelCreateEditModal}
              />
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalCreateEdit;
